import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Layer, Rect, Stage, Text, Transformer } from 'react-konva';
import useImage from 'use-image';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import type { TextItem } from '../types';
import { limitDrag } from '../utils/canvas';

type KonvaTextType = Konva.Text;
type CanvasStageProps = Record<string, unknown>;

const CanvasStage: React.FC<CanvasStageProps> = (props: CanvasStageProps) => {
  const stageRef = useCanvasStore((s) => s.stageRef);
  const logoRef = useCanvasStore((s) => s.logoRef);
  const trRef = useCanvasStore((s) => s.trRef);
  const textRefs = useCanvasStore((s) => s.textRefs);
  const bgImage = useCanvasStore((s) => s.bgImage);
  const logoImage = useCanvasStore((s) => s.logoImage);
  const backgroundColor = useCanvasStore((s) => s.backgroundColor);
  const texts = useCanvasStore((s) => s.texts);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const logoProps = useCanvasStore((s) => s.logoProps);
  const setLogoProps = useCanvasStore((s) => s.setLogoProps);
  const selectedId = useCanvasStore((s) => s.selectedId);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const isEditingTextId = useCanvasStore((s) => s.isEditingTextId);
  const previewMode = useCanvasStore((s) => s.previewMode);
  const handleTextTransform = useCanvasStore((s) => s.handleTextTransform);
  const handleLogoDoubleClick = useCanvasStore((s) => s.handleLogoDoubleClick);
  const handleTextDoubleClick = useCanvasStore((s) => s.handleTextDoubleClick);
  const saveStateToHistory = useCanvasStore((s) => s.saveStateToHistory);
  const updateImage = useCanvasStore((s) => s.updateImage);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const updateElementOrder = useCanvasStore((s) => s.updateElementOrder);

  const [bg] = useImage(bgImage ?? '', 'anonymous');
  const [logo] = useImage(logoImage ?? '', 'anonymous');

  const [imageMap, setImageMap] = useState<Record<string, HTMLImageElement | null>>({});

  useEffect(() => {
    if (!canvasImages.length) return;

    const loadImages = async () => {
      const newImageMap: Record<string, HTMLImageElement | null> = {};

      const loadPromises = canvasImages.map(
        (image) =>
          new Promise<void>((resolve) => {
            const img = document.createElement('img');
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              newImageMap[image.url] = img;
              resolve();
            };
            img.onerror = () => {
              newImageMap[image.url] = null;
              resolve();
            };
            img.src = image.url;
          })
      );

      await Promise.all(loadPromises);
      setImageMap(newImageMap);
    };

    loadImages();
  }, [canvasImages]);

  const calculateTextHeight = useCallback((textItem: TextItem): number => {
    const tempNode = new Konva.Text({
      text: textItem.text || ' ',
      fontSize: textItem.fontSize,
      fontFamily: textItem.fontFamily,
      width: textItem.width,
      align: textItem.align,
      lineHeight: textItem.lineHeight,
      letterSpacing: textItem.letterSpacing,
      padding: 0,
    });
    const height = tempNode.height();
    tempNode.destroy();
    return height;
  }, []);

  const handleDragStart = useCallback(() => {
    saveStateToHistory();
  }, [saveStateToHistory]);

  const handleLogoClick = useCallback(() => {
    if (!previewMode) {
      setSelectedId('logo');
    }
  }, [previewMode, setSelectedId]);

  const handleLogoDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      setLogoProps((prev) => ({ ...prev, x: e.target.x(), y: e.target.y() }));
    },
    [setLogoProps]
  );

  const handleDirectLogoTransform = useCallback(
    (e: KonvaEventObject<Event>) => {
      saveStateToHistory();
      if (!logoRef.current) return;

      const konvaLogo = logoRef.current as unknown as Konva.Image;
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const width = node.width() * scaleX;
      const height = node.height() * scaleY;
      const x = node.x();
      const y = node.y();

      konvaLogo.scaleX(1);
      konvaLogo.scaleY(1);
      konvaLogo.width(width);
      konvaLogo.height(height);

      setLogoProps((prev) => ({
        ...prev,
        width,
        height,
        x,
        y,
      }));

      const layer = konvaLogo.getLayer();
      if (layer) layer.batchDraw();
    },
    [logoRef, setLogoProps, saveStateToHistory]
  );

  const renderTexts = useMemo(() => {
    return texts.map((textItem) => {
      const { id, text, x, y, width, color, align, fontFamily, fontSize, lineHeight, letterSpacing } = textItem;
      const exactHeight = calculateTextHeight(textItem);

      return (
        <Text
          key={id}
          id={`text-${id}`}
          ref={(node) => {
            if (node) {
              textRefs.current?.set(id, node as unknown as KonvaTextType);
              node.height(exactHeight);
              node.padding(0);
            } else if (textRefs.current) {
              textRefs.current.delete(id);
            }
          }}
          text={text}
          fontSize={fontSize}
          fill={color}
          x={x}
          y={y}
          width={width}
          height={exactHeight}
          align={align}
          fontFamily={fontFamily}
          lineHeight={lineHeight}
          letterSpacing={letterSpacing}
          padding={0}
          draggable={!previewMode}
          onClick={(e) => {
            if (!previewMode) {
              e.cancelBubble = true;
              setSelectedId(id);
            }
          }}
          onDblClick={(e) => {
            if (!previewMode) {
              e.cancelBubble = true;
              handleTextDoubleClick(id);
            }
          }}
          dragBoundFunc={(pos) => limitDrag(pos, width, fontSize * 1.5)}
          onDragStart={handleDragStart}
          onDragEnd={(e) => {
            setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, x: e.target.x(), y: e.target.y() } : t)));
          }}
          onTransformStart={handleDragStart}
          onTransformEnd={() => handleTextTransform(id)}
          opacity={isEditingTextId === id ? 0 : 1}
        />
      );
    });
  }, [
    texts,
    previewMode,
    calculateTextHeight,
    setTexts,
    setSelectedId,
    textRefs,
    isEditingTextId,
    handleTextDoubleClick,
    handleTextTransform,
    handleDragStart,
  ]);

  useEffect(() => {
    if (!logo) return;

    const hasValidDimensions =
      logoProps.width > 10 &&
      logoProps.height > 10 &&
      logoProps.width < CANVAS_WIDTH &&
      logoProps.height < CANVAS_HEIGHT;

    if (hasValidDimensions) return;

    const maxInitialWidth = CANVAS_WIDTH / 4;
    const aspectRatio = logo.height / logo.width;

    let initialWidth = Math.min(logo.width, maxInitialWidth);
    let initialHeight = initialWidth * aspectRatio;

    if (initialHeight > CANVAS_HEIGHT / 3) {
      initialHeight = CANVAS_HEIGHT / 3;
      initialWidth = initialHeight / aspectRatio;
    }

    setLogoProps((prev) => ({
      ...prev,
      width: initialWidth,
      height: initialHeight,
    }));
  }, [logo, logoProps, setLogoProps]);

  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;

    const transformer = trRef.current as unknown as Konva.Transformer;
    transformer.nodes([]);

    if (selectedId === 'logo' && logoRef.current && logo) {
      transformer.nodes([logoRef.current as unknown as Konva.Node]);
      transformer.keepRatio(true);
      transformer.enabledAnchors([
        'top-left',
        'top-center',
        'top-right',
        'middle-left',
        'middle-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ]);
    } else if (selectedId && selectedId !== 'logo' && selectedId !== 'canvas' && !isEditingTextId) {
      const textNode = stageRef.current.findOne(`#text-${selectedId}`);
      if (textNode) {
        transformer.nodes([textNode as Konva.Node]);
        transformer.keepRatio(false);
        transformer.enabledAnchors([
          'top-left',
          'top-center',
          'top-right',
          'middle-left',
          'middle-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ]);
      }
    }

    transformer.rotateEnabled(false);
    transformer.padding(1);
    transformer.borderStroke('#3498db');
    transformer.borderStrokeWidth(2);
    transformer.anchorStroke('#3498db');
    transformer.anchorFill('#fff');
    transformer.anchorSize(8);

    const layer = transformer.getLayer();
    if (layer) layer.batchDraw();
  }, [selectedId, isEditingTextId, logo, logoRef, trRef, stageRef, texts]);

  const bgImageRef = useRef<Konva.Image | null>(null);

  const handleCanvasClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (previewMode) return;

      const clickedOnBg =
        e.target === stageRef.current?.findOne('Rect') ||
        e.target === bgImageRef.current ||
        e.target === e.target.getStage();

      if (clickedOnBg) {
        setSelectedId('canvas');
        if (trRef.current) {
          trRef.current.nodes([]);
          trRef.current.getLayer()?.batchDraw();
        }
      }
    },
    [previewMode, setSelectedId, trRef, stageRef]
  );

  const handleImageTransform = useCallback(
    (id: string) => {
      saveStateToHistory();

      const imageNode = stageRef.current?.findOne(`#${id}`);
      if (!imageNode) return;

      const konvaImage = imageNode as unknown as Konva.Image;
      const newWidth = Math.round(konvaImage.width() * konvaImage.scaleX());
      const newHeight = Math.round(konvaImage.height() * konvaImage.scaleY());
      const originalX = konvaImage.x();
      const originalY = konvaImage.y();

      konvaImage.scaleX(1);
      konvaImage.scaleY(1);

      updateImage(id, {
        x: originalX,
        y: originalY,
        width: newWidth,
        height: newHeight,
      });
    },
    [saveStateToHistory, stageRef, updateImage]
  );

  const imageRefs = useRef(new Map());
  const renderImages = useMemo(() => {
    return canvasImages
      .map((image) => {
        const img = imageMap[image.url];

        if (image.deleted) return null;

        const rotation = image.rotation || 0;

        return (
          <Image
            key={image.id}
            id={image.id}
            ref={(node) => {
              if (node) {
                imageRefs.current.set(image.id, node);

                if (stageRef.current) {
                  const layer = node.getLayer();
                  if (layer) layer.batchDraw();
                }
              }
            }}
            image={img || undefined}
            x={image.x}
            y={image.y}
            width={image.width}
            height={image.height}
            opacity={image.opacity || 1}
            rotation={rotation}
            draggable={!previewMode}
            onClick={() => {
              if (!previewMode) {
                setSelectedId(image.id);

                if (trRef.current) {
                  const imageNode = stageRef.current?.findOne(`#${image.id}`);
                  if (imageNode) {
                    const transformer = trRef.current as unknown as Konva.Transformer;
                    transformer.enabledAnchors(['top-left', 'top-right', 'bottom-left', 'bottom-right']);
                    transformer.nodes([imageNode as Konva.Node]);
                    transformer.getLayer()?.batchDraw();
                  }
                }
              }
            }}
            dragBoundFunc={(pos) => limitDrag(pos, image.width, image.height)}
            onDragStart={handleDragStart}
            onDragEnd={(e) => {
              saveStateToHistory();
              updateImage(image.id, { x: e.target.x(), y: e.target.y() });
            }}
            onTransformStart={handleDragStart}
            onTransformEnd={() => handleImageTransform(image.id)}
          />
        );
      })
      .filter(Boolean);
  }, [
    canvasImages,
    imageMap,
    previewMode,
    setSelectedId,
    handleDragStart,
    saveStateToHistory,
    updateImage,
    handleImageTransform,
    trRef,
    stageRef,
  ]);

  useEffect(() => {
    updateElementOrder();
  }, [texts, canvasImages, logoImage, updateElementOrder]);

  return (
    <Stage
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      ref={stageRef}
      onClick={handleCanvasClick}
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        maxWidth: '100%',
      }}
    >
      <Layer>
        <Rect x={0} y={0} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill={backgroundColor} />
        {bg && (
          <Image
            ref={bgImageRef}
            image={bg}
            x={0}
            y={0}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            listening={true}
            onClick={(e: KonvaEventObject<MouseEvent>) => {
              e.cancelBubble = true;
              setSelectedId('canvas');
              if (trRef.current) {
                trRef.current.nodes([]);
                trRef.current.getLayer()?.batchDraw();
              }
            }}
          />
        )}
      </Layer>
      <Layer>
        {logo && (
          <Image
            id="logo"
            ref={logoRef}
            image={logo}
            x={logoProps.x}
            y={logoProps.y}
            width={logoProps.width}
            height={logoProps.height}
            draggable={!previewMode}
            onClick={handleLogoClick}
            onDblClick={!previewMode ? handleLogoDoubleClick : undefined}
            dragBoundFunc={(pos) => limitDrag(pos, logoProps.width, logoProps.height)}
            onDragStart={handleDragStart}
            onDragEnd={handleLogoDragEnd}
            onTransformStart={handleDragStart}
            onTransformEnd={handleDirectLogoTransform}
          />
        )}

        {renderTexts}
        {renderImages}
        <Transformer ref={trRef} boundBoxFunc={(oldBox, newBox) => newBox} />
      </Layer>
    </Stage>
  );
};

export default CanvasStage;
